/**
 * BrightPath - Expense Types
 * BP-04-04: Expenses Frontend
 */

export type ExpenseCategory =
  | 'medical'
  | 'therapy'
  | 'equipment'
  | 'medication'
  | 'transportation'
  | 'home_modification'
  | 'legal'
  | 'insurance'
  | 'other';

export type ClaimStatus =
  | 'not_submitted'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'denied'
  | 'appealed'
  | 'partial';

export type RecurrencePattern =
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'annually';

export interface Expense {
  id: string;
  familyId: string;
  childId: string | null;
  enteredById: string;
  category: ExpenseCategory;
  subcategory: string | null;
  description: string;
  amount: number;
  currency: string;
  expenseDate: string;
  vendorName: string | null;
  paymentMethod: string | null;
  isInsuranceCovered: boolean;
  insuranceAmount: number | null;
  outOfPocket: number;
  claimNumber: string | null;
  claimStatus: ClaimStatus | null;
  isTaxDeductible: boolean;
  taxCategory: string | null;
  receiptDocumentId: string | null;
  isRecurring: boolean;
  recurrencePattern: RecurrencePattern | null;
  recurrenceEndDate: string | null;
  notes: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  // Expanded relations
  child?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  enteredBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  receiptDocument?: {
    id: string;
    filename: string;
    originalFilename: string;
  };
}

export interface ExpenseCreate {
  childId?: string | null;
  category: ExpenseCategory;
  subcategory?: string | null;
  description: string;
  amount: number;
  currency?: string;
  expenseDate: string;
  vendorName?: string | null;
  paymentMethod?: string | null;
  isInsuranceCovered?: boolean;
  insuranceAmount?: number | null;
  outOfPocket?: number;
  claimNumber?: string | null;
  claimStatus?: ClaimStatus | null;
  isTaxDeductible?: boolean;
  taxCategory?: string | null;
  receiptDocumentId?: string | null;
  isRecurring?: boolean;
  recurrencePattern?: RecurrencePattern | null;
  recurrenceEndDate?: string | null;
  notes?: string | null;
  tags?: string[];
}

export interface ExpenseUpdate extends Partial<ExpenseCreate> {}

export interface ExpenseFilters {
  category?: ExpenseCategory | null;
  childId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  isTaxDeductible?: boolean | null;
  isInsuranceCovered?: boolean | null;
  vendorName?: string | null;
  tags?: string[];
  search?: string | null;
}

export interface ExpenseListParams extends ExpenseFilters {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ExpenseListResponse {
  items: Expense[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ExpenseSummary {
  totalExpenses: number;
  totalOutOfPocket: number;
  totalInsuranceCovered: number;
  totalTaxDeductible: number;
  expenseCount: number;
  byCategory: CategorySummary[];
  byChild: ChildSummary[];
  byMonth: MonthSummary[];
}

export interface CategorySummary {
  category: ExpenseCategory;
  total: number;
  count: number;
  outOfPocket: number;
  insuranceCovered: number;
  percentage: number;
}

export interface ChildSummary {
  childId: string;
  childName: string;
  total: number;
  count: number;
  percentage: number;
}

export interface MonthSummary {
  year: number;
  month: number;
  total: number;
  count: number;
  outOfPocket: number;
  insuranceCovered: number;
}

export interface ExpenseTrend {
  date: string;
  total: number;
  outOfPocket: number;
  insuranceCovered: number;
  count: number;
}

// Category display metadata
export const EXPENSE_CATEGORIES: Record<ExpenseCategory, { label: string; icon: string; color: string }> = {
  medical: { label: 'Medical', icon: 'Stethoscope', color: '#7A9E7E' },
  therapy: { label: 'Therapy', icon: 'Brain', color: '#6B8DD6' },
  equipment: { label: 'Equipment', icon: 'Accessibility', color: '#C4785A' },
  medication: { label: 'Medication', icon: 'Pill', color: '#9DB8A0' },
  transportation: { label: 'Transportation', icon: 'Car', color: '#445566' },
  home_modification: { label: 'Home Modification', icon: 'Home', color: '#D99B7E' },
  legal: { label: 'Legal', icon: 'Scale', color: '#2B3A4D' },
  insurance: { label: 'Insurance', icon: 'Shield', color: '#8B8178' },
  other: { label: 'Other', icon: 'MoreHorizontal', color: '#5C7D60' },
};

export const CLAIM_STATUS_LABELS: Record<ClaimStatus, { label: string; color: string }> = {
  not_submitted: { label: 'Not Submitted', color: '#8B8178' },
  submitted: { label: 'Submitted', color: '#6B8DD6' },
  in_review: { label: 'In Review', color: '#C4785A' },
  approved: { label: 'Approved', color: '#4CAF9E' },
  denied: { label: 'Denied', color: '#E57373' },
  appealed: { label: 'Appealed', color: '#D99B7E' },
  partial: { label: 'Partial', color: '#9DB8A0' },
};

export const RECURRENCE_PATTERNS: Record<RecurrencePattern, string> = {
  weekly: 'Weekly',
  biweekly: 'Every 2 Weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
};
